/**
 * CotPaymentSettings — Ajustes de cobro (copiados del Creador de Itinerarios):
 * moneda USD/EUR, tipo de cambio congelado (BCE), comisión PayPal 3 %, reparto por viajero,
 * datos de viajeros recibidos.
 */
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Check, Loader2, Plus, RefreshCw, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api, apiErrorMessage } from "@/lib/api";

const money = (n, cur = "EUR") => new Intl.NumberFormat("es-ES", { style: "currency", currency: cur }).format(n || 0);

export function CotPaymentSettings({ cotId, publicToken }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["cot-payment-settings", cotId],
    queryFn: async () => (await api.get(`/cotizaciones/${cotId}/payment-settings`)).data,
  });
  const [fxInput, setFxInput] = useState("");
  const [shares, setShares] = useState(null);

  useEffect(() => {
    if (q.data && shares === null) setShares((q.data.payer_shares || []).map((s) => ({ id: s.id, name: s.name, email: s.email || "", amount_eur: s.amount_eur })));
  }, [q.data, shares]);

  const save = useMutation({
    mutationFn: async (body) => (await api.put(`/cotizaciones/${cotId}/payment-settings`, body)).data,
    onSuccess: (d) => {
      qc.setQueryData(["cot-payment-settings", cotId], d);
      qc.invalidateQueries({ queryKey: ["paypal-status", publicToken] });
      setShares((d.payer_shares || []).map((s) => ({ id: s.id, name: s.name, email: s.email || "", amount_eur: s.amount_eur })));
      toast.success("Ajustes de cobro guardados");
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (q.isLoading || !q.data) return <p className="text-xs text-muted-foreground">Cargando ajustes de cobro…</p>;
  const d = q.data;
  const usd = d.currency === "USD";
  const sharesSum = (shares || []).reduce((a, s) => a + Number(s.amount_eur || 0), 0);
  const sharesOk = !shares?.length || Math.abs(sharesSum - d.total_eur) < 0.01;

  return (
    <div className="space-y-3 rounded-lg border p-3" data-testid="cot-payment-settings">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Ajustes de cobro</Label>
        <div className="flex overflow-hidden rounded-md border text-xs" data-testid="cot-pay-currency-toggle">
          {["USD", "EUR"].map((c) => (
            <button key={c} type="button" onClick={() => save.mutate({ payment_currency: c })} data-testid={`cot-pay-currency-${c}`}
              className={`px-3 py-1 font-medium transition-colors ${d.currency === c ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
              {c === "USD" ? "USD $" : "EUR €"}
            </button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">
        {usd ? "El cliente paga en dólares; los precios internos siguen en euros. EUR es ideal si el cliente es europeo (evita la comisión de conversión de PayPal)."
             : "El cliente paga en euros, sin conversión."}
      </p>

      {usd && (
        <div className={`rounded-md border p-2.5 ${d.fx_saved ? "bg-muted/30" : "border-amber-300 bg-amber-50"}`} data-testid="cot-pay-fx">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {d.fx_saved ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-700" />}
            <span className="font-medium">Tipo de cambio EUR→USD: {Number(d.fx_rate).toFixed(4)}</span>
            <Badge variant="outline" className="text-[9px]">{d.fx_saved ? (d.fx_source === "manual" ? "manual · guardado" : "BCE · guardado") : "tasa NO guardada (BCE de hoy)"}</Badge>
            <span className="ml-auto text-muted-foreground">BCE hoy: {Number(d.ecb?.rate).toFixed(4)}</span>
          </div>
          {!d.fx_saved && <p className="mt-1 text-[11px] text-amber-800">Si no la guardas, el importe del link cambiará cada día con el mercado. Guárdala para congelar el precio que ve el cliente.</p>}
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => save.mutate({ fx_rate: d.ecb?.rate, fx_rate_source: "ecb" })} data-testid="cot-pay-fx-save-ecb">
              <RefreshCw className="mr-1 h-3 w-3" />Guardar tasa BCE ({Number(d.ecb?.rate).toFixed(4)})
            </Button>
            <Input type="number" step="0.0001" min="0.5" max="2" placeholder="Manual, p. ej. 1.15" value={fxInput} onChange={(e) => setFxInput(e.target.value)} className="h-7 w-36 text-xs" data-testid="cot-pay-fx-manual" />
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!fxInput} onClick={() => { save.mutate({ fx_rate: Number(fxInput), fx_rate_source: "manual" }); setFxInput(""); }} data-testid="cot-pay-fx-save-manual">Guardar manual</Button>
            {d.fx_saved && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => save.mutate({ clear_fx: true })} data-testid="cot-pay-fx-clear">Volver a automático</Button>}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between rounded-md border p-2.5">
        <div className="text-xs">
          <p className="font-medium">Total que ve el cliente: <span data-testid="cot-pay-total-charge">{money(d.total_charge, d.currency)}</span></p>
          <p className="text-muted-foreground">{money(d.total_eur, "EUR")} internos{d.paypal_fee ? " · el PVP ya incluye el recargo PayPal (+3 %) activado en el panel Coste" : " · sin recargo PayPal (se activa en el panel Coste)"}</p>
        </div>
      </div>

      <div className="rounded-md border p-2.5" data-testid="cot-pay-shares">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-xs font-medium"><Users className="h-3.5 w-3.5" />Reparto por viajero</p>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShares([...(shares || []), { name: "", email: "", amount_eur: 0 }])} data-testid="cot-pay-share-add"><Plus className="mr-1 h-3 w-3" />Añadir pagador</Button>
        </div>
        <p className="text-[11px] text-muted-foreground">Cada viajero paga su parte (30 % de depósito sobre su importe). En la página de pago elegirá «¿Quién eres?».</p>
        {(shares || []).map((s, i) => (
          <div key={s.id || i} className="mt-1.5 grid grid-cols-[1fr_1fr_110px_28px] gap-1.5" data-testid={`cot-pay-share-row-${i}`}>
            <Input placeholder="Nombre" value={s.name} onChange={(e) => setShares(shares.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className="h-7 text-xs" data-testid={`cot-pay-share-name-${i}`} />
            <Input placeholder="Email (opcional)" value={s.email} onChange={(e) => setShares(shares.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} className="h-7 text-xs" />
            <Input type="number" step="0.01" placeholder="€" value={s.amount_eur} onChange={(e) => setShares(shares.map((x, j) => j === i ? { ...x, amount_eur: e.target.value } : x))} className="h-7 text-xs" data-testid={`cot-pay-share-amount-${i}`} />
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setShares(shares.filter((_, j) => j !== i))}><Trash2 className="h-3 w-3" /></Button>
          </div>
        ))}
        {shares?.length > 0 && (
          <div className="mt-2 flex items-center justify-between text-[11px]">
            <span className={sharesOk ? "text-emerald-700" : "text-red-600"} data-testid="cot-pay-shares-sum">Suma {money(sharesSum)} / {money(d.total_eur)}{!sharesOk && " — debe coincidir con el total"}</span>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShares(shares.map((s) => ({ ...s, amount_eur: Math.round((d.total_eur / shares.length) * 100) / 100 })))}>Repartir a partes iguales</Button>
              <Button size="sm" className="h-7 text-xs" disabled={!sharesOk || save.isPending} onClick={() => save.mutate({ payer_shares: shares.map((s) => ({ ...s, amount_eur: Number(s.amount_eur) })) })} data-testid="cot-pay-shares-save">
                {save.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Guardar reparto"}
              </Button>
            </div>
          </div>
        )}
        {shares?.length === 0 && d.payer_shares?.length > 0 && (
          <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => save.mutate({ payer_shares: [] })}>Quitar reparto</Button>
        )}
        {(d.payer_shares || []).some((s) => s.paid_eur > 0) && (
          <div className="mt-2 space-y-0.5 text-[11px]">
            {d.payer_shares.map((s) => <p key={s.id}>{s.name}: pagado {money(s.paid, d.currency)} · pendiente {money(s.remaining_eur, "EUR")}</p>)}
          </div>
        )}
      </div>

      {d.traveler_info?.submitted_at && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-2.5 text-xs" data-testid="cot-pay-traveler-info">
          <p className="font-medium text-emerald-800">Datos de viajeros recibidos · {new Date(d.traveler_info.submitted_at).toLocaleDateString("es-ES")}</p>
          {(d.traveler_info.persons || []).map((p, i) => (
            <p key={i} className="text-muted-foreground">{p.first_name} {p.last_name}{p.date_of_birth ? ` · ${p.date_of_birth}` : ""}{p.nationality ? ` · ${p.nationality}` : ""}{p.passport_number ? ` · pasaporte ${p.passport_number}${p.passport_expiry ? ` (cad. ${p.passport_expiry})` : ""}` : ""}{p.dietary ? ` · dieta: ${p.dietary}` : ""}</p>
          ))}
          {d.traveler_info.emergency_contact_name && <p className="text-muted-foreground">Emergencia: {d.traveler_info.emergency_contact_name} {d.traveler_info.emergency_contact_phone}</p>}
        </div>
      )}
    </div>
  );
}

export default CotPaymentSettings;
